// Pure (React-free) logic behind «Откуда клиент»: the draft <-> API payload
// conversion shared by the quick-create page and the booking edit form.

import type { BookingAgent, BookingAgentPayload, CommissionType } from "@/lib/types";

export type ClientOrigin = "direct" | "agent";

export interface AgentDraft {
  origin: ClientOrigin;
  agentId: string | null;
  type: CommissionType;
  /** What is typed: an amount in the booking's currency, or a percent. */
  value: string;
}

export const emptyAgentDraft = (): AgentDraft => ({
  origin: "direct",
  agentId: null,
  type: "percent",
  value: "",
});

/** "12,5" / "12.5" -> 12.5; null when blank, not a number or not positive. */
export function parsePositive(text: string): number | null {
  const n = Number(text.trim().replace(",", "."));
  return text.trim() !== "" && Number.isFinite(n) && n > 0 ? n : null;
}

/** The draft an existing booking's agent block edits from. */
export function agentDraftFromBooking(agent: BookingAgent | null | undefined): AgentDraft {
  if (!agent) return emptyAgentDraft();
  if (agent.commission_type === "none") {
    return { origin: "agent", agentId: agent.agent_id, type: "none", value: "" };
  }
  const percent = agent.commission_type === "percent";
  const value = percent
    ? String(agent.commission_percent ?? "")
    : agent.commission_amount_minor != null
      ? (agent.commission_amount_minor / 100).toFixed(2)
      : "";
  return { origin: "agent", agentId: agent.agent_id, type: agent.commission_type, value };
}

export interface AgentDraftIssues {
  agent: boolean;
  value: boolean;
}

/** What is still missing on an «От турагента» draft (nothing for «Напрямую»). */
export function agentDraftIssues(d: AgentDraft): AgentDraftIssues {
  if (d.origin === "direct") return { agent: false, value: false };
  if (d.type === "none") return { agent: !d.agentId, value: false };
  const n = parsePositive(d.value);
  return {
    agent: !d.agentId,
    value: n === null || (d.type === "percent" && n > 100),
  };
}

export function agentDraftIsValid(d: AgentDraft): boolean {
  const issues = agentDraftIssues(d);
  return !issues.agent && !issues.value;
}

/** The API body; `null` = the client came directly. Call only on a valid draft. */
export function agentDraftToPayload(d: AgentDraft): BookingAgentPayload | null {
  if (d.origin === "direct" || !d.agentId) return null;
  if (d.type === "none") return { travel_agent_id: d.agentId, commission_type: "none" };
  const n = parsePositive(d.value);
  if (n === null) return null;
  return d.type === "fixed"
    ? {
        travel_agent_id: d.agentId,
        commission_type: "fixed",
        commission_amount_minor: Math.round(n * 100),
      }
    : { travel_agent_id: d.agentId, commission_type: "percent", commission_percent: n };
}

/** Has the owner changed anything since the form opened? */
export function agentDraftChanged(a: AgentDraft, b: AgentDraft): boolean {
  if (a.origin !== b.origin) return true;
  if (a.origin === "direct") return false;
  return (
    a.agentId !== b.agentId ||
    a.type !== b.type ||
    (a.type !== "none" && parsePositive(a.value) !== parsePositive(b.value))
  );
}
